/**
 * 极简紫韵模版 - 单页
 * 
 * 设计理念：左侧文章目录 + 右侧正文排版
 * 与所有现有单页模板（渐变头部+白卡）完全不同：
 * - 左侧：Sticky文章目录（TOC自动提取h2/h3标题）
 * - 右侧：正文内容 + 紫色左侧边栏装饰条
 * - 极简排版，纯阅读体验
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PurpleNavbar from "@/templates/purple/components/Navbar";
import PurpleFooter from "@/templates/purple/components/Footer";
import { getSinglePage, type SinglePage as SinglePageType } from "@/utils/api";

export default function PurpleSinglePage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<SinglePageType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getSinglePage(slug)
      .then((data) => {
        setPage(data);
        if (data?.title) document.title = data.title;
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [slug]);

  // 提取正文中的标题作为目录
  const toc = useMemo(() => {
    if (!page?.content) return [];
    const headings: { id: string; text: string; level: number }[] = [];
    // 简单提取h2和h3
    const regex = /<(h[23])[^>]*>(.*?)<\/h[23]>/gi;
    let match;
    while ((match = regex.exec(page.content)) !== null) {
      const text = match[2].replace(/<[^>]+>/g, '').trim();
      if (text) {
        headings.push({ id: `heading-${headings.length}`, text, level: match[1] === 'h2' ? 2 : 3 });
      }
    }
    return headings;
  }, [page?.content]);

  // 给content中的标题加上id
  const enhancedContent = useMemo(() => {
    if (!page?.content) return '';
    let idx = 0;
    return page.content.replace(/<(h[23])([^>]*)>(.*?)<\/h[23]>/gi, (match, tag, attrs, text) => {
      const id = `heading-${idx++}`;
      return `<${tag}${attrs} id="${id}">${text}</${tag}>`;
    });
  }, [page?.content]);

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PurpleNavbar />
        <div className="pt-24 pb-16 animate-pulse">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex gap-8">
              <div className="hidden lg:block w-48 space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-100 rounded" style={{ width: `${80 - i * 10}%` }} />
                ))}
              </div>
              <div className="flex-1 space-y-4">
                <div className="h-8 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-3/4" />
              </div>
            </div>
          </div>
        </div>
        <PurpleFooter />
      </div>
    );
  }

  // 未找到
  if (!page) {
    return (
      <div className="min-h-screen bg-white">
        <PurpleNavbar />
        <div className="pt-28 pb-20 text-center">
          <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-3xl bg-purple-50">
            <i className="ri-file-damage-line text-3xl text-purple-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">页面未找到</h1>
          <p className="text-sm text-gray-500 mb-6">您访问的单页不存在或已被删除</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white text-sm font-bold rounded-2xl hover:from-purple-700 hover:to-violet-700 transition-all shadow-md">
            <i className="ri-home-4-line" />返回首页
          </Link>
        </div>
        <PurpleFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PurpleNavbar />

      {/* 顶部标题 */}
      <section className="pt-24 pb-8 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* 面包屑 */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-400 mb-4">
            <Link to="/" className="hover:text-purple-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-purple-600 font-semibold">{page.title}</span>
          </nav>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">
            {page.title}
          </h1>

          <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-violet-500 rounded-full mt-4" />
        </div>
      </section>

      {/* 内容区：左侧目录 + 右侧正文 */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex gap-8 lg:gap-12">
          {/* 左侧目录（仅桌面端） */}
          {toc.length > 0 && (
            <aside className="hidden lg:block w-48 shrink-0">
              <div className="sticky top-24">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-list-unordered text-purple-400" />
                  目录
                </h4>
                <nav className="space-y-1">
                  {toc.map((item, idx) => (
                    <a
                      key={idx}
                      href={`#${item.id}`}
                      className={`block text-sm py-1.5 transition-colors hover:text-purple-600 ${
                        item.level === 2
                          ? 'text-gray-600 font-medium'
                          : 'text-gray-400 pl-4 text-xs'
                      }`}
                    >
                      {item.text}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          {/* 右侧正文 */}
          <article className="flex-1 min-w-0">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-10">
              {enhancedContent ? (
                <div
                  className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-purple-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-800 prose-blockquote:border-l-purple-400 prose-blockquote:bg-purple-50/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-code:bg-purple-50 prose-code:text-purple-600 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-img:rounded-2xl prose-img:shadow-md"
                  dangerouslySetInnerHTML={{ __html: enhancedContent }}
                />
              ) : (
                <div className="text-center py-12">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-purple-50 flex items-center justify-center">
                    <i className="ri-article-line text-xl text-purple-400" />
                  </div>
                  <p className="text-gray-400">暂无内容</p>
                </div>
              )}
            </div>

            {/* 返回首页 */}
            <div className="mt-8 text-center">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors group">
                <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform" />
                返回首页
              </Link>
            </div>
          </article>
        </div>
      </div>

      <PurpleFooter />
    </div>
  );
}
